import React from 'react';
import MobileLocationField from './form-fields/mobile/MobileLocationField';
import MobileRadiusField from './form-fields/mobile/MobileRadiusField';
import MobilePriceRangeField from './form-fields/mobile/MobilePriceRangeField';
import MobileBedroomsField from './form-fields/mobile/MobileBedroomsField';
import MobileHouseTypeField from './form-fields/mobile/MobileHouseTypeField';
import MobileAdditionalFiltersField from './form-fields/mobile/MobileAdditionalFiltersField';

function MobileFiltersOverlay({
  isOpen,
  onClose,
  searchMode,
  maxData,
  filterData,
  filters,
  onAddressChange,
  onAddressSelect,
  MAPBOX_TOKEN,
  onRadiusChange,
  onMinPriceChange,
  onMaxPriceChange,
  onMinBedroomsChange,
  onMaxBedroomsChange,
  onHouseTypeChange,
  onFeatureChange,
  onBuyingSchemeChange,
  onSubmit,
}) {
  if (!isOpen) return null;

  const handleClearFilters = () => {
    // Reset all filters to their default values
    onAddressChange('');
    onAddressSelect('', null);
    onRadiusChange('20');
    onMinPriceChange('');
    onMaxPriceChange('');
    onMinBedroomsChange('');
    onMaxBedroomsChange('');

    // Only clear house types and additional filters if not in developments mode
    if (searchMode !== 'developments') {
      // Clear all selected house types
      filters.houseTypes.forEach((type) => {
        onHouseTypeChange(type);
      });

      // Clear all selected features
      filters.features.forEach((feature) => {
        onFeatureChange(feature);
      });

      // Clear all selected buying schemes
      filters.buyingSchemes.forEach((scheme) => {
        onBuyingSchemeChange(scheme);
      });
    }
  };

  return (
    <div className="lg:hidden fixed inset-0 bg-white z-50">
      <div className="h-full overflow-y-scroll pb-32">
        <div className="text-white bg-blue-700 flex items-center text-center p-[13px]">
          <h2 className="text-sm font-medium ml-auto">Filters</h2>

          <button
            type="button"
            onClick={onClose}
            className="ml-auto"
            aria-label="Close filters"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="25"
              height="25"
              viewBox="0 0 25 25"
              fill="none"
              aria-hidden="true"
              focusable="false"
            >
              <rect
                x="0.414062"
                y="23"
                width="32"
                height="2"
                transform="rotate(-45 0.414062 23)"
                fill="currentColor"
              />
              <rect
                x="1.41406"
                width="32"
                height="2"
                transform="rotate(45 1.41406 0)"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>

        <div className="[&>div]:border-b-2 [&>div]:border-[#EBEBEB] [&>div]:py-4">
          {/* Location Field */}
          <MobileLocationField
            address={filters.address}
            onAddressChange={onAddressChange}
            onAddressSelect={onAddressSelect}
            MAPBOX_TOKEN={MAPBOX_TOKEN}
          />

          {/* Radius Filter */}
          <MobileRadiusField
            radius={filters.radius}
            onRadiusChange={onRadiusChange}
          />

          {/* Price Range Filter */}
          <MobilePriceRangeField
            maxPriceData={maxData.price ?? 2000000}
            minPrice={filters.minPrice}
            maxPrice={filters.maxPrice}
            onMinPriceChange={onMinPriceChange}
            onMaxPriceChange={onMaxPriceChange}
          />

          {/* Bedrooms Filter */}
          <MobileBedroomsField
            maxBedroomsData={maxData.bedrooms ?? 10}
            minBedrooms={filters.minBedrooms}
            maxBedrooms={filters.maxBedrooms}
            onMinBedroomsChange={onMinBedroomsChange}
            onMaxBedroomsChange={onMaxBedroomsChange}
          />

          {/* House Type Checkboxes - Disabled for developments */}
          <MobileHouseTypeField
            houseTypeData={filterData?.houseTypes || []}
            selectedHouseTypes={filters.houseTypes}
            onHouseTypeChange={onHouseTypeChange}
            disabled={searchMode === 'developments'}
          />

          {/* Additional Filters Checkboxes - Disabled for developments */}
          <MobileAdditionalFiltersField
            filterData={filterData}
            selectedFeatures={filters.features}
            selectedBuyingSchemes={filters.buyingSchemes}
            onFeatureChange={onFeatureChange}
            onBuyingSchemeChange={onBuyingSchemeChange}
            disabled={searchMode === 'developments'}
          />
        </div>

        {/* Mobile Search Button and Clear Filters */}
        <div className="py-6 w-full px-4 flex items-center gap-4 fixed bottom-0 shadow-[0_0_20px_0_rgba(0,0,0,0.10)] bg-white">
          <button
            type="button"
            onClick={onSubmit}
            className="w-full bg-pink-400 hover:bg-blue-700 flex-1 transition-colors text-white font-medium py-4 rounded"
          >
            Search
          </button>
          <button
            type="button"
            onClick={handleClearFilters}
            className="text-blue-700 text-sm underline flex-1"
          >
            Clear Filters
          </button>
        </div>
      </div>
    </div>
  );
}

export default MobileFiltersOverlay;
